import { useEffect, useRef, useState, type ReactNode } from "react";
import { ApiError, api, getSessionToken, type Registration } from "../api/client";
import { buildEnrollmentMaterial, type EnrollmentMaterial } from "../lib/registration-crypto";
import {
  clearPendingRegistration,
  readPendingRegistration,
  writePendingRegistration,
  type PendingRegistration
} from "../lib/pending-registration";

// v0.6 vouching: manage the account's registered attestation keys. Enrollment
// needs the subject's private key to sign a proof-of-possession challenge, so
// the "generate & enroll" flow mints an Ed25519 keypair in the browser, signs
// the challenge, and shows the keypair ONCE for the subject to save. The private
// key never reaches Sequesign. Because that key lives only in the browser, it is
// also stashed in sessionStorage before the POST (see pending-registration.ts)
// and resurfaced via a recovery banner, so an unmount/reload mid-enrollment
// can't strand it.

type Role = "approver" | "counterparty";
type PartyType = "human" | "agent";

const IDENTITY_MAX = 256;

type CreateState = {
  role: Role;
  partyType: PartyType;
  identity: string;
  submitting: boolean;
  error: string | null;
  // Generated ONCE and stashed (state + sessionStorage) BEFORE the POST, so an
  // ambiguous failure never strands the key. A retry reuses it (idempotent on
  // the server). Cleared when role/party_type/identity change (it's bound to
  // those).
  material: EnrollmentMaterial | null;
  materialDownloaded: boolean;
  registration: Registration | null;
  refCopied: boolean;
  confirmingClose: boolean;
};

// Build + trigger the one-time keypair download. Bundles BOTH PEM blocks: the
// SDK's sign_locally needs KeyMaterial with publicKeyPem AND privateKeyPem, and
// the public key is not recoverable from the dashboard afterwards.
function downloadKeypairFile(material: EnrollmentMaterial, identity: string, role: string): void {
  const idPart =
    identity
      .trim()
      .replace(/[^a-zA-Z0-9._-]+/g, "-")
      .replace(/^-+|-+$/g, "") || "subject";
  const filename = `sequesign-attestation-keypair-${idPart}.pem`;
  const contents =
    `# Sequesign attestation keypair for ${identity.trim()} (${role})\n` +
    `# Use with the SDK as the approver/counterparty KeyMaterial:\n` +
    `#   { privateKeyPem, publicKeyPem }  (the two PEM blocks below).\n` +
    `${material.privateKeyPem}${material.publicKeyPem}`;
  const blob = new Blob([contents], { type: "application/x-pem-file" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function RegistrationsPage() {
  const [registrations, setRegistrations] = useState<Registration[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [createState, setCreateState] = useState<CreateState | null>(null);
  // Hidden-but-accessible: revoked identities are excluded by default and shown
  // when this toggle is on (mirrors the API-keys "show revoked" pattern).
  const [showRevoked, setShowRevoked] = useState(false);
  // A keypair generated in a prior (possibly interrupted) enrollment that was
  // never saved. Read from sessionStorage on mount and whenever the dialog
  // closes; surfaced as a recovery banner until downloaded or discarded.
  const [recovered, setRecovered] = useState<PendingRegistration | null>(null);
  // Skip the POST if the page unmounted during async key generation (the key is
  // already persisted by then in the in-flight case, so it stays recoverable).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    setRecovered(readPendingRegistration());
    return () => {
      mountedRef.current = false;
    };
  }, []);

  async function load() {
    try {
      const result = await api.listRegistrations({ includeRevoked: showRevoked });
      setRegistrations(result.registrations);
      setLoadError(null);
      return result.registrations;
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : "Could not load registrations.");
      throw err;
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const result = await api.listRegistrations({ includeRevoked: showRevoked });
        if (!cancelled) {
          setRegistrations(result.registrations);
          setLoadError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof ApiError ? err.message : "Could not load registrations.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showRevoked]);

  async function handleRevoke(id: string): Promise<void> {
    // Throws on failure so the row surfaces the message; reloads on success so
    // the row reflects its new state (or disappears when revoked are hidden).
    await api.revokeRegistration(id);
    await load().catch(() => undefined);
  }

  function openCreate() {
    setCreateState({
      role: "approver",
      partyType: "human",
      identity: "",
      submitting: false,
      error: null,
      material: null,
      materialDownloaded: false,
      registration: null,
      refCopied: false,
      confirmingClose: false
    });
  }

  // Close the dialog and re-read sessionStorage: if a generated key was left
  // unsaved (force-closed), the recovery banner picks it up.
  function closeCreate() {
    setCreateState(null);
    setRecovered(readPendingRegistration());
  }

  async function submitCreate() {
    if (!createState) return;
    const identity = createState.identity.trim();
    if (identity.length === 0 || identity.length > IDENTITY_MAX) return;
    const role = createState.role;
    const partyType = createState.partyType;
    const existingMaterial = createState.material;
    const alreadyDownloaded = createState.materialDownloaded;
    const persistedPartyType = role === "approver" ? partyType : undefined;
    // Capture the enrolling session's token now: if the user signs out while the
    // POST is in flight, the stash must stay bound to THIS session (not become a
    // no-session entry), so the key is never surfaced to or cleared by another.
    const ownerToken = getSessionToken();
    // Disable the dialog synchronously BEFORE any await so it can't be closed or
    // edited while generation / the POST is in flight.
    setCreateState((s) => (s ? { ...s, submitting: true, error: null } : s));
    try {
      const material =
        existingMaterial ??
        (await buildEnrollmentMaterial(
          role === "approver" ? { role, partyType, identity } : { role: "counterparty", identity }
        ));
      // If the page unmounted during async key generation, do NOT post — no
      // registration was created, so nothing to recover.
      if (!mountedRef.current) return;
      // Persist the key BEFORE the POST so an unmount/reload while the request is
      // in flight (the registration may still be created) leaves it recoverable.
      // Skip it once the key has already been downloaded (e.g. download-then-retry):
      // it's saved, so re-stashing would resurrect a stale recovery banner for it.
      if (!alreadyDownloaded) {
        writePendingRegistration(
          { material, role, partyType: persistedPartyType, identity },
          ownerToken
        );
      }
      setCreateState((s) => (s ? { ...s, material } : s));

      const registration = await api.createRegistration({
        role,
        party_type: persistedPartyType,
        identity,
        public_key: material.publicKeyPem,
        subject_signature: material.subjectSignature
      });
      // Update the stash with the issued registration (so the recovery banner can
      // show the ref too). Harmless if the page already unmounted. Skipped if the
      // key was already downloaded (nothing left to recover).
      if (!alreadyDownloaded) {
        writePendingRegistration(
          { material, role, partyType: persistedPartyType, identity, registration },
          ownerToken
        );
      }
      if (!mountedRef.current) return;
      setCreateState((s) => (s ? { ...s, submitting: false, error: null, registration } : s));
      await load().catch(() => undefined);
    } catch (err) {
      if (!mountedRef.current) return;
      // invalid_identity is rejected server-side BEFORE any row is created, so
      // the registration definitely does not exist and the generated key (bound
      // to a rejected identity) is useless. Discard it and reset material so the
      // identity field unlocks — otherwise fieldsLocked stays true and "Retry"
      // just re-sends the same bad identity, which is the stuck state users hit.
      if (err instanceof ApiError && err.code === "invalid_identity") {
        clearPendingRegistration();
        const message =
          role === "approver"
            ? "Identity must be a valid approver id (lowercase, e.g. brent@acme.com). Correct it and enroll again."
            : "Identity must be a canonical counterparty id (lowercase dotted hostname, e.g. vendor.acme.com). Correct it and enroll again.";
        setCreateState((s) =>
          s
            ? { ...s, submitting: false, error: message, material: null, materialDownloaded: false }
            : s
        );
        return;
      }
      const message =
        err instanceof ApiError
          ? err.code === "key_already_registered"
            ? "That key is already registered to a different identity for this account."
            : err.message
          : "Could not reach Sequesign. The registration may or may not have been created — download the keypair below, then retry (it's safe to retry; the same key is reused).";
      setCreateState((s) => (s ? { ...s, submitting: false, error: message } : s));
    }
  }

  function tryCloseCreate() {
    if (!createState) return;
    // Warn if a key was generated but not saved AND the enrollment was attempted
    // (success, or an ambiguous error where the registration may exist). Even on
    // force-close the key stays recoverable via the banner, but warn first.
    const keyAtRisk =
      !!createState.material &&
      !createState.materialDownloaded &&
      (createState.registration !== null || createState.error !== null);
    if (keyAtRisk) {
      setCreateState({ ...createState, confirmingClose: true });
      return;
    }
    closeCreate();
  }

  function downloadFromDialog() {
    if (!createState?.material) return;
    downloadKeypairFile(createState.material, createState.identity, createState.role);
    clearPendingRegistration();
    setCreateState((s) => (s ? { ...s, materialDownloaded: true } : s));
  }

  async function copyRef() {
    if (!createState?.registration) return;
    try {
      await navigator.clipboard.writeText(createState.registration.identity_proof.ref);
      setCreateState((s) => (s ? { ...s, refCopied: true } : s));
    } catch {
      // Clipboard unavailable; the user can select the value by hand.
    }
  }

  function retryLoad() {
    setLoading(true);
    load()
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }

  if (loadError && !registrations && !recovered) {
    return (
      <div className="panel">
        <h2 className="section-title">Registered identities</h2>
        <div className="error">{loadError}</div>
        <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={retryLoad}>
          Retry
        </button>
      </div>
    );
  }

  const rows = registrations ?? [];

  return (
    <>
      <div className="panel">
        <div className="section-header">
          <h2 className="section-title">Registered identities</h2>
          <button
            className="btn"
            onClick={openCreate}
            disabled={(loading && !registrations) || recovered !== null}
            title={
              recovered !== null
                ? "Download or discard the unsaved attestation key above before enrolling another."
                : undefined
            }
          >
            Enroll a key
          </button>
        </div>
        <p className="muted" style={{ marginTop: 0 }}>
          Registering an approver or counterparty key binds it to a named identity. Attestations
          signed by a registered key verify as <strong>present_verified</strong> instead of an
          unverifiable name claim. Registrations are immutable and verify offline against
          Sequesign's published key.
        </p>
      </div>

      {recovered && !createState ? (
        <RecoveryBanner
          pending={recovered}
          onDownload={() => {
            downloadKeypairFile(recovered.material, recovered.identity, recovered.role);
            clearPendingRegistration();
            setRecovered(null);
          }}
          onDiscard={() => {
            clearPendingRegistration();
            setRecovered(null);
          }}
        />
      ) : null}

      <div className="panel">
        <div
          className="section-header"
          style={{ marginTop: 0, marginBottom: 8, justifyContent: "flex-end" }}
        >
          <label
            className="muted"
            style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}
          >
            <input
              type="checkbox"
              checked={showRevoked}
              onChange={(e) => setShowRevoked(e.target.checked)}
            />
            Show revoked
          </label>
        </div>
        {loading && !registrations ? (
          <p className="muted">Loading registrations…</p>
        ) : loadError && !registrations ? (
          <div>
            <div className="error">{loadError}</div>
            <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={retryLoad}>
              Retry
            </button>
          </div>
        ) : rows.length === 0 ? (
          <p className="empty-state">
            No registered identities yet. Click "Enroll a key" to register an approver or
            counterparty key.
          </p>
        ) : (
          <div className="key-list">
            {rows.map((r) => (
              <RegistrationRow key={r.id} registration={r} onRevoke={handleRevoke} />
            ))}
          </div>
        )}
        {loadError && registrations ? (
          <div style={{ marginTop: 12 }}>
            <div className="error">{loadError}</div>
            <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={retryLoad}>
              Retry
            </button>
          </div>
        ) : null}
      </div>

      {createState ? (
        <CreateModal
          state={createState}
          onChange={setCreateState}
          onSubmit={submitCreate}
          onClose={tryCloseCreate}
          onForceClose={closeCreate}
          onDownloadKeypair={downloadFromDialog}
          onCopyRef={copyRef}
        />
      ) : null}
    </>
  );
}

function RecoveryBanner(props: {
  pending: PendingRegistration;
  onDownload: () => void;
  onDiscard: () => void;
}) {
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [refCopied, setRefCopied] = useState(false);
  const p = props.pending;

  async function copyRef() {
    if (!p.registration) return;
    try {
      await navigator.clipboard.writeText(p.registration.identity_proof.ref);
      setRefCopied(true);
      window.setTimeout(() => setRefCopied(false), 2000);
    } catch {
      // Clipboard unavailable; the user can select from the expanded display.
    }
  }

  return (
    <div className="panel modal-warning">
      <h2 className="section-title">Unsaved attestation key</h2>
      <p className="warning-text" style={{ marginTop: 0 }}>
        A keypair for <strong>{p.identity}</strong> ({p.role}) was generated but not saved
        {p.registration ? " after the identity was registered" : ""}. Download it now — the subject
        needs it to sign attestations, and it can't be recovered once discarded.
      </p>
      {/* If the POST had succeeded before the interruption, the issued ref is in
          the stash — surface it here too, so it's recoverable even if the
          registrations list is currently unavailable (mirrors the success modal). */}
      {p.registration ? (
        <div className="key-display">
          <div className="label">identity_proof.ref (attach to attestations)</div>
          <div className="key-row">
            <code className="key-value" style={{ wordBreak: "break-all" }}>
              {p.registration.identity_proof.ref}
            </code>
            <button className="btn btn-secondary" onClick={copyRef}>
              {refCopied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      ) : null}
      <div style={{ display: "flex", gap: 12 }}>
        <button className="btn" onClick={props.onDownload}>
          Download keypair (.pem)
        </button>
        {confirmingDiscard ? (
          <button className="btn btn-danger" onClick={props.onDiscard}>
            Confirm discard
          </button>
        ) : (
          <button className="btn btn-secondary" onClick={() => setConfirmingDiscard(true)}>
            Discard
          </button>
        )}
      </div>
    </div>
  );
}

function RegistrationRow(props: {
  registration: Registration;
  onRevoke: (id: string) => Promise<void>;
}) {
  const r = props.registration;
  const revoked = r.revoked_at !== null;
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function copyRef() {
    try {
      await navigator.clipboard.writeText(r.identity_proof.ref);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable; the user can select from the expanded display.
    }
  }

  async function doRevoke() {
    setWorking(true);
    setError(null);
    try {
      // On success the page reloads the list, so this row re-renders as revoked
      // (when shown) or disappears (when revoked are hidden).
      await props.onRevoke(r.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not revoke this identity.");
    } finally {
      // Always reset: if the revoke succeeded but the list reload failed (the
      // page shows that error separately), the row stays mounted as active, so
      // it must return to a usable state rather than stick at "Revoking…".
      setWorking(false);
      setConfirming(false);
    }
  }

  return (
    <div className="key-item" style={revoked ? { opacity: 0.6 } : undefined}>
      <div className="key-item-main">
        <div className="key-item-header">
          <span className="key-item-label">{r.identity}</span>
          <span className="badge" aria-label={`Role: ${r.role}`}>
            {r.role}
          </span>
          {r.party_type ? (
            <span className="badge" aria-label={`Party type: ${r.party_type}`}>
              {r.party_type}
            </span>
          ) : null}
          {revoked ? <span className="badge">revoked</span> : null}
        </div>
        <div className="key-item-meta">
          <span className="key-item-prefix" title={r.subject_key_fingerprint}>
            {r.subject_key_fingerprint.slice(0, "sha256:".length + 16)}…
          </span>
          <span title={r.created_at}>Registered {formatRelative(r.created_at)}</span>
          {revoked && r.revoked_at ? (
            <span title={r.revoked_at}>Revoked {formatRelative(r.revoked_at)}</span>
          ) : null}
        </div>
        <details className="registration-proof">
          <summary>identity_proof.ref</summary>
          <code className="key-value" style={{ wordBreak: "break-all" }}>
            {r.identity_proof.ref}
          </code>
        </details>
        {error ? (
          <div className="error" style={{ marginTop: 8 }}>
            {error}
          </div>
        ) : null}
      </div>
      <div className="key-item-actions">
        <button className="btn btn-secondary" onClick={copyRef}>
          {copied ? "Copied" : "Copy proof"}
        </button>
        {!revoked ? (
          confirming ? (
            <>
              <button className="btn btn-danger" onClick={doRevoke} disabled={working}>
                {working ? "Revoking…" : "Confirm revoke"}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setConfirming(false)}
                disabled={working}
              >
                Cancel
              </button>
            </>
          ) : (
            <button className="btn btn-secondary" onClick={() => setConfirming(true)}>
              Revoke
            </button>
          )
        ) : null}
      </div>
    </div>
  );
}

function CreateModal(props: {
  state: CreateState;
  onChange: (next: CreateState) => void;
  onSubmit: () => void;
  onClose: () => void;
  onForceClose: () => void;
  onDownloadKeypair: () => void;
  onCopyRef: () => void;
}) {
  const s = props.state;
  const identityTrim = s.identity.trim();
  const identityInvalid = identityTrim.length === 0 || identityTrim.length > IDENTITY_MAX;
  // Once a key is generated, lock the signed fields: a different identity must
  // not be enrolled into the same in-flight slot (it would clobber the unsaved
  // key whose registration may already exist). To change identity, Cancel — the
  // generated key is preserved via the recovery banner — then enroll fresh.
  const fieldsLocked = s.submitting || s.material !== null;

  if (s.confirmingClose) {
    return (
      <ModalShell warning>
        <h2 className="section-title">Discard the keypair?</h2>
        <p className="warning-text">
          You haven't downloaded the attestation keypair. You can still recover it from the banner
          after closing, but it's safest to download it now.
          {s.registration
            ? " The identity is registered"
            : " If the enrollment went through, the identity is registered"}{" "}
          and the subject needs this key to sign attestations. Continue?
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button
            className="btn btn-secondary"
            onClick={() => props.onChange({ ...s, confirmingClose: false })}
          >
            Cancel
          </button>
          <button className="btn btn-danger" onClick={() => props.onForceClose()}>
            Close anyway
          </button>
        </div>
      </ModalShell>
    );
  }

  if (s.registration) {
    const r = s.registration;
    return (
      <ModalShell warning>
        <h2 className="section-title">Key registered</h2>
        <p className="warning-text">
          <strong>Save the keypair now — it's shown only once.</strong> The subject ({r.identity})
          needs it to sign attestations. Sequesign never sees it.
        </p>
        <div className="key-display">
          <div className="label">Attestation keypair (.pem)</div>
          <button className="btn btn-secondary" onClick={props.onDownloadKeypair}>
            {s.materialDownloaded ? "Keypair downloaded" : "Download keypair (.pem)"}
          </button>
        </div>
        <div className="key-display">
          <div className="label">identity_proof.ref (attach to attestations)</div>
          <div className="key-row">
            <code className="key-value" style={{ wordBreak: "break-all" }}>
              {r.identity_proof.ref}
            </code>
            <button className="btn btn-secondary" onClick={props.onCopyRef}>
              {s.refCopied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <button className="btn" onClick={props.onClose}>
            Done
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell>
      <h2 className="section-title">Enroll a key</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Your browser generates an Ed25519 keypair and signs a proof-of-possession challenge. The
        keypair is shown once for you to save and never reaches Sequesign.
      </p>
      <div className="field">
        <span className="label">Role</span>
        <div className="modal-radio-group">
          <label>
            <input
              type="radio"
              name="reg-role"
              value="approver"
              checked={s.role === "approver"}
              onChange={() => props.onChange({ ...s, role: "approver", error: null })}
              disabled={fieldsLocked}
            />
            Approver
          </label>
          <label>
            <input
              type="radio"
              name="reg-role"
              value="counterparty"
              checked={s.role === "counterparty"}
              onChange={() => props.onChange({ ...s, role: "counterparty", error: null })}
              disabled={fieldsLocked}
            />
            Counterparty
          </label>
        </div>
      </div>
      {s.role === "approver" ? (
        <div className="field">
          <span className="label">Party type</span>
          <div className="modal-radio-group">
            <label>
              <input
                type="radio"
                name="reg-party-type"
                value="human"
                checked={s.partyType === "human"}
                onChange={() => props.onChange({ ...s, partyType: "human" })}
                disabled={fieldsLocked}
              />
              Human
            </label>
            <label>
              <input
                type="radio"
                name="reg-party-type"
                value="agent"
                checked={s.partyType === "agent"}
                onChange={() => props.onChange({ ...s, partyType: "agent" })}
                disabled={fieldsLocked}
              />
              Agent
            </label>
          </div>
        </div>
      ) : null}
      <div className="field">
        <label className="label" htmlFor="reg-identity">
          Identity
        </label>
        <input
          id="reg-identity"
          className="input"
          autoFocus
          value={s.identity}
          maxLength={IDENTITY_MAX}
          placeholder={s.role === "approver" ? "brent@acme.com" : "vendor.acme.com"}
          onChange={(e) => props.onChange({ ...s, identity: e.target.value, error: null })}
          disabled={fieldsLocked}
        />
        <span className="char-count">
          {s.role === "approver"
            ? "The approver_id the attestation will carry (lowercase email-style label)."
            : "The counterparty_id the attestation will carry (lowercase dotted hostname)."}
        </span>
      </div>
      {s.error ? <div className="error">{s.error}</div> : null}
      {s.error && s.material ? (
        <div className="key-display">
          <div className="label">Generated attestation keypair (.pem)</div>
          <button className="btn btn-secondary" onClick={props.onDownloadKeypair}>
            {s.materialDownloaded ? "Keypair downloaded" : "Download keypair (.pem)"}
          </button>
        </div>
      ) : null}
      <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
        <button className="btn btn-secondary" onClick={props.onClose} disabled={s.submitting}>
          Cancel
        </button>
        <button className="btn" disabled={s.submitting || identityInvalid} onClick={props.onSubmit}>
          {s.submitting ? "Enrolling…" : s.error && s.material ? "Retry" : "Generate & enroll"}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell(props: { children: ReactNode; warning?: boolean }) {
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className={props.warning ? "modal modal-warning" : "modal"}>{props.children}</div>
    </div>
  );
}

// Local relative-time formatter (mirrors ApiKeysPage); returns the raw value on
// a parse failure so the tooltip still shows the absolute timestamp.
function formatRelative(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  const diff = Date.now() - parsed;
  if (diff < 0) return "just now";
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} ${days === 1 ? "day" : "days"} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} ${months === 1 ? "month" : "months"} ago`;
  const years = Math.floor(days / 365);
  return `${years} ${years === 1 ? "year" : "years"} ago`;
}
